import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AngularMaterialModule } from 'src/app/module/angular-material/angular-material.module';
import { GoogleAutocompleteComponent } from 'src/app/module/google-map/component/google-autocomplete/google-autocomplete.component';
import { Coordinate } from 'src/app/module/google-map/interface/google-map.interface';
import { NewOrderState } from 'src/app/module/new-order/state/new-order.state';
import { ReadOnlyDirective } from 'src/app/module/order/directive/read-only.directive';
import { AddComparableService } from 'src/app/module/order/service/add-comp.service';
import { CoordinateComponent } from 'src/app/module/profet-control-widget/component/coordinate/coordinate.component';
import { ProfetControlWidgetModule } from 'src/app/module/profet-control-widget/profet-control-widget.module';
import { PropertyAttribute } from 'src/app/module/property/interface/property.interface';
import { ScenarioState } from 'src/app/module/scenario/state/scenario.state';
import { ConfirmPropertyParam } from 'src/app/module/short-term-rental/interface/short-term-rental.interface';
import { ShortTermRentalState } from 'src/app/module/short-term-rental/state/short-term-rental.state';
import { ProgressSpinnerComponent } from 'src/app/shared/components/progress-spinner/progress-spinner.component';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { AppState } from 'src/app/shared/state/app.state';
import { CompanyState } from 'src/app/shared/state/company.state';
import {
  AddComparableParams,
  AddCompValidationStatus,
  AddNewCustomComparableParams,
  AddNewCustomSubjectParams,
  ValuationType,
} from '../../interface/order.interface';
import { ComparableService } from '../../service/comparable.service';
import { OrderService } from '../../service/order.service';
import { ApproachFactoryState } from '../../state/approach-factory.state';
import { OrderState } from '../../state/order.state';
import { SalesApproachState } from '../../state/sales-approach.state';

@Component({
  selector: 'app-add-custom-property',
  standalone: true,
  imports: [
    CommonModule,
    AngularMaterialModule,
    ReactiveFormsModule,
    FormsModule,
    SharedModule,
    ProfetControlWidgetModule,
    GoogleAutocompleteComponent,
    ProgressSpinnerComponent,
    ReadOnlyDirective,
  ],
  templateUrl: './add-custom-property.component.html',
  styleUrls: ['./add-custom-property.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCustomPropertyComponent implements OnDestroy {
  valuationType = ValuationType;
  customCompSearchForm: FormGroup = this._fb.group({});

  coordinateCtrl = new FormControl<Coordinate | null>(null, Validators.required);
  propertyTypeOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['propertySubType'] ?? [];
  zoningComplianceOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['zoningCompliance'] ?? [];
  associationYNOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['associationYN'] ?? [];
  derivedAssociationFeeFrequencyOptionList =
    this.companyState.getEnumConfig(this.orderState.reportType)['associationFeeFrequency'] ?? [];
  rentalTypeOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['rentalType'] ?? [];
  sourceTypeOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['source'] ?? [];
  poolYNOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['poolYN'] ?? [];
  viewFactorsOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['viewFactor'] ?? [];
  derivedLotFeaturesOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['lotFeatures'] ?? [];
  locationRatingOptionList = this.companyState.getEnumConfig(this.orderState.reportType)['locationRating'] ?? [];

  onDestroyNotifier$ = new Subject<void>();
  validationResult: AddCompValidationStatus | null = null;
  isSpecificComparable: boolean = false;
  modifiedValues: Partial<Record<PropertyAttribute, any>> = {};
  initialValues: any;
  addButtonLabel: string = '';

  @Input() comparableId!: string;
  @Input() isSubject: boolean = false;
  @Input() showBackButton: boolean = false;
  @Output() close: EventEmitter<never> = new EventEmitter<never>();
  @Output() addSubjectManually = new EventEmitter<AddNewCustomSubjectParams>();
  @ViewChild('autocomplete') autocompleteComponent!: GoogleAutocompleteComponent;
  @ViewChild(CoordinateComponent) coordinateComponent?: CoordinateComponent;

  constructor(
    private _cdr: ChangeDetectorRef,
    public companyState: CompanyState,
    private _fb: NonNullableFormBuilder,
    private addComparableService: AddComparableService,
    private comparableService: ComparableService,
    private newOrderState: NewOrderState,
    private orderService: OrderService,
    private progressLoaderService: ProgressLoaderService,
    private scenarioState: ScenarioState,
    private snackBarService: SnackBarService,
    public appState: AppState,
    public orderState: OrderState,
    public salesApproachState: SalesApproachState,
    public shortTermRentalState: ShortTermRentalState,
    public approachFactoryState: ApproachFactoryState
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.checkAndFillForm();

    this.initialValues = this.customCompSearchForm.value;

    this.coordinateCtrl.valueChanges.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((coordinate) => {
      this.customCompSearchForm
        .get('addressInfo')
        ?.get('Latitude')
        ?.setValue(coordinate?.lat?.toString() ?? '');
      this.customCompSearchForm
        .get('addressInfo')
        ?.get('Longitude')
        ?.setValue(coordinate?.lng?.toString() ?? '');
    });

    this.customCompSearchForm.valueChanges.subscribe(() => {
      this.modifiedValues = {};
      this._flattenFormControls(this.customCompSearchForm, this.initialValues, this.modifiedValues);
    });

    this.customCompSearchForm
      .get('compAttributes.StandardStatus')
      ?.valueChanges.pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((value) => {
        if (value?.toLowerCase() == 'sold') {
          this.customCompSearchForm.get('compAttributes.ListPrice')?.clearValidators();
          this.customCompSearchForm.get('compAttributes.ListPrice')?.updateValueAndValidity();
        } else {
          this.customCompSearchForm.get('compAttributes.ClosePrice')?.clearValidators();
          this.customCompSearchForm.get('compAttributes.ClosePrice')?.updateValueAndValidity();
        }
        this._cdr.detectChanges();
      });

    this.customCompSearchForm
      .get('addressInfo')
      ?.valueChanges.pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe(() => {
        this.validationResult = null;
      });

    this.orderState.specificComparable$.pipe().subscribe(() => {
      this.checkAndFillForm();
    });
  }

  initializeForm() {
    const addressInfo = this._fb.group({
      propertyStreetAddress: [''],
      StreetNumber: [''],
      StreetName: [''],
      UnitNumber: [''],
      propertyCity: [''],
      propertyState: [''],
      propertyZip: [''],
      Latitude: [''],
      Longitude: [''],
    });

    this.customCompSearchForm.addControl('addressInfo', addressInfo);

    if (
      this.orderState.orderSetting?.[this.approachFactoryState.activeApproachKey]
        ?.showAdditionalAttrsOnAddCustomProperty !== false
    ) {
      const compAttributes = this._fb.group({
        StandardStatus: [''],
        ListPrice: [''],
        ClosePrice: [''],
        PropertySubType: [''],
        LotSizeSquareFeet: [''],
        ArchitecturalStyle: [''],
        LivingArea: [''],
        YearBuilt: [''],
        RoomsTotal: [''],
        BedroomsTotal: [''],
        BathroomsFullCalculated: [''],
        BathroomsHalfCalculated: [''],
        BelowGradeTotalArea: [''],
        BelowGradeFinishedArea: [''],
        MLSListingNumber: [''],
        MlsBoard: [''],
        ParcelNumber: [''],
        TaxLegalDescription: [''],
        ZoningDescription: [''],
        ZoningCompliance: [''],
        Zoning: [''],
        Owner1FullName: [''],
        Owner2FullName: [''],
        AssociationYN: [''],
        AssociationName: [''],
        AssociationFee: [''],
        derivedAssociationFeeFrequency: [''],
        Basement: [''],
        GarageSpaces: [''],
        ParkingTotal: [''],
        ListPriceNightly: [''],
        OccupancyRate: [''],
        RentalType: [''],
        RentalSource: [''],
        RentalSourceUrl: [''],
        PoolFeatures: [''],
        PoolPrivateYN: [''],
        derivedLotFeatures: [''],
        LocationRating: [''],
        derivedView: [''],
      });
      this.customCompSearchForm.addControl('compAttributes', compAttributes);
    }
  }

  checkAndFillForm() {
    this.validationResult = null;
    this.isSpecificComparable = this.orderState.specificComparable ? true : false;
    this.addButtonLabel = this.isSubject
      ? 'Confirm'
      : this.isSpecificComparable
        ? this.comparableId
          ? 'Save Property'
          : 'Add Comparable'
        : this.comparableId
          ? 'Save Property'
          : 'Add Custom Comparable';

    const comparable = this.orderState.specificComparable;

    if (comparable) {
      const streetAddress = comparable?.StreetAddress ?? '';
      const streetNumberMatch = streetAddress?.match(/^\d+/) ?? '';
      const streetNumber = streetNumberMatch ? streetNumberMatch[0] : '';
      const streetName = streetAddress?.replace(streetNumber, '').trim() ?? '';

      if (comparable?.Latitude && comparable.Longitude) {
        this.coordinateCtrl.setValue({ lat: comparable.Latitude, lng: comparable.Longitude });
      }

      // updating addressInfo
      this.customCompSearchForm.get('addressInfo')?.setValue({
        propertyStreetAddress: comparable?.Address ?? '',
        StreetNumber: streetNumber ?? '',
        StreetName: streetName ?? '',
        UnitNumber: comparable?.UnitNumber ?? '',
        propertyCity: comparable?.City ?? '',
        propertyState: comparable?.StateOrProvince ?? '',
        propertyZip: comparable?.PostalCode ?? '',

        Latitude: comparable?.Latitude ?? '',
        Longitude: comparable?.Longitude ?? '',
      });

      // updating compAttributes
      if (
        this.orderState.orderSetting?.[this.approachFactoryState.activeApproachKey]
          ?.showAdditionalAttrsOnAddCustomProperty !== false
      ) {
        this.customCompSearchForm.get('compAttributes')?.patchValue({
          PropertySubType: comparable?.PropertySubType ?? '',
          LotSizeSquareFeet: comparable?.LotSizeSquareFeet ?? '',
          LivingArea: comparable?.LivingArea ?? '',
          BedroomsTotal: comparable?.BedroomsTotal ?? '',
          BathroomsFullCalculated: comparable?.BathroomsFullCalculated ?? '',
          BathroomsHalfCalculated: comparable?.BathroomsHalfCalculated ?? '',
          ParcelNumber: comparable?.ParcelNumber ?? '',
          Owner1FullName: comparable?.OwnerName ?? '',
          MLSListingNumber: comparable?.MLSListingNumber ?? '',
          MlsBoard: comparable?.MlsBoard ?? '',
        });
      }
    } else if (this.isSubject && this.newOrderState.searchPropertyQuery?.customData) {
      if (
        this.newOrderState.searchPropertyQuery?.customData?.Latitude &&
        this.newOrderState.searchPropertyQuery?.customData?.Longitude
      ) {
        this.coordinateCtrl.setValue({
          lat: this.newOrderState.searchPropertyQuery?.customData?.Latitude,
          lng: this.newOrderState.searchPropertyQuery?.customData?.Longitude,
        });
      }
      // updating addressInfo
      this.customCompSearchForm.get('addressInfo')?.setValue({
        propertyStreetAddress: this.newOrderState.searchPropertyQuery?.customData?.propertyStreetAddress ?? '',
        StreetNumber: this.newOrderState.searchPropertyQuery?.customData.StreetNumber ?? '',
        StreetName: this.newOrderState.searchPropertyQuery?.customData?.StreetName ?? '',
        UnitNumber: this.newOrderState.searchPropertyQuery?.customData?.UnitNumber ?? '',
        propertyCity: this.newOrderState.searchPropertyQuery?.customData?.propertyCity ?? '',
        propertyState: this.newOrderState.searchPropertyQuery?.customData?.propertyState ?? '',
        propertyZip: this.newOrderState.searchPropertyQuery?.customData?.propertyZip ?? '',
        Latitude: this.newOrderState.searchPropertyQuery?.customData?.Latitude ?? '',
        Longitude: this.newOrderState.searchPropertyQuery?.customData?.Longitude ?? '',
      });

      // updating compAttributes
      if (
        this.orderState.orderSetting?.[this.approachFactoryState.activeApproachKey]
          ?.showAdditionalAttrsOnAddCustomProperty !== false
      ) {
        this.customCompSearchForm.get('compAttributes')?.patchValue({
          StandardStatus: this.newOrderState.searchPropertyQuery?.customData?.StandardStatus ?? '',
          ListPrice: this.newOrderState.searchPropertyQuery?.customData?.ListPrice ?? '',
          ClosePrice: this.newOrderState.searchPropertyQuery?.customData?.ClosePrice ?? '',
          PropertySubType: this.newOrderState.searchPropertyQuery?.customData?.PropertySubType ?? '',
          LotSizeSquareFeet: this.newOrderState.searchPropertyQuery?.customData?.LotSizeSquareFeet ?? '',
          ArchitecturalStyle: this.newOrderState.searchPropertyQuery?.customData?.ArchitecturalStyle ?? '',
          LivingArea: this.newOrderState.searchPropertyQuery?.customData?.LivingArea ?? '',
          YearBuilt: this.newOrderState.searchPropertyQuery?.customData?.YearBuilt ?? '',
          RoomsTotal: this.newOrderState.searchPropertyQuery?.customData?.RoomsTotal ?? '',
          BedroomsTotal: this.newOrderState.searchPropertyQuery?.customData?.BedroomsTotal ?? '',
          BathroomsFullCalculated: this.newOrderState.searchPropertyQuery?.customData?.BathroomsFullCalculated ?? '',
          BathroomsHalfCalculated: this.newOrderState.searchPropertyQuery?.customData?.BathroomsHalfCalculated ?? '',
          BelowGradeTotalArea: this.newOrderState.searchPropertyQuery?.customData?.BelowGradeTotalArea ?? '',
          BelowGradeFinishedArea: this.newOrderState.searchPropertyQuery?.customData?.BelowGradeFinishedArea ?? '',
          ParcelNumber: this.newOrderState.searchPropertyQuery?.customData?.ParcelNumber ?? '',
          TaxLegalDescription: this.newOrderState.searchPropertyQuery?.customData?.TaxLegalDescription ?? '',
          ZoningDescription: this.newOrderState.searchPropertyQuery?.customData?.ZoningDescription ?? '',
          ZoningCompliance: this.newOrderState.searchPropertyQuery?.customData?.ZoningCompliance ?? '',
          Zoning: this.newOrderState.searchPropertyQuery?.customData?.Zoning ?? '',
          Owner1FullName: this.newOrderState.searchPropertyQuery?.customData?.Owner1FullName ?? '',
          Owner2FullName: this.newOrderState.searchPropertyQuery?.customData?.Owner2FullName ?? '',
          AssociationYN: this.newOrderState.searchPropertyQuery?.customData?.AssociationYN ?? '',
          AssociationName: this.newOrderState.searchPropertyQuery?.customData?.AssociationName ?? '',
          AssociationFee: this.newOrderState.searchPropertyQuery?.customData?.AssociationFee ?? '',
          derivedAssociationFeeFrequency:
            this.newOrderState.searchPropertyQuery?.customData?.derivedAssociationFeeFrequency ?? '',
          Basement: this.newOrderState.searchPropertyQuery?.customData?.Basement ?? '',
          GarageSpaces: this.newOrderState.searchPropertyQuery?.customData?.GarageSpaces ?? '',
          ParkingTotal: this.newOrderState.searchPropertyQuery?.customData?.ParkingTotal ?? '',
          ListPriceNightly: this.newOrderState.searchPropertyQuery?.customData?.ListPriceNightly ?? '',
          OccupancyRate: this.newOrderState.searchPropertyQuery?.customData?.OccupancyRate ?? '',
          RentalType: this.newOrderState.searchPropertyQuery?.customData?.RentalType ?? '',
          RentalSource: this.newOrderState.searchPropertyQuery?.customData?.RentalSource ?? '',
          RentalSourceUrl: this.newOrderState.searchPropertyQuery?.customData?.RentalSourceUrl ?? '',
          PoolFeatures: this.newOrderState.searchPropertyQuery?.customData?.PoolFeatures ?? '',
          PoolPrivateYN: this.newOrderState.searchPropertyQuery?.customData?.PoolPrivateYN ?? '',
          derivedLotFeatures: this.newOrderState.searchPropertyQuery?.customData?.derivedLotFeatures ?? '',
          LocationRating: this.newOrderState.searchPropertyQuery?.customData?.LocationRating ?? '',
          derivedView: this.newOrderState.searchPropertyQuery?.customData?.derivedView ?? '',

          MLSListingNumber: this.newOrderState.searchPropertyQuery?.customData?.MLSListingNumber ?? '',
          MlsBoard: this.newOrderState.searchPropertyQuery?.customData?.MlsBoard ?? '',
        });
      }
    } else {
      this.onResetForm();
    }

    this._cdr.detectChanges();
  }

  fillAddressComp(addressComp: any) {
    this.validationResult = null;

    // A place without usable coordinates must leave the control empty so the required
    // validator still blocks the save, rather than storing a "NaN" string.
    const latitude = Number(addressComp?.latitude);
    const longitude = Number(addressComp?.longitude);
    const hasCoordinate =
      addressComp?.latitude != null &&
      addressComp?.longitude != null &&
      Number.isFinite(latitude) &&
      Number.isFinite(longitude);

    this.coordinateCtrl.setValue(hasCoordinate ? { lat: latitude.toFixed(6), lng: longitude.toFixed(6) } : null);

    this.customCompSearchForm.get('addressInfo')?.patchValue({
      propertyStreetAddress: addressComp?.streetAddress || '',
      StreetNumber: addressComp?.streetNumber || '',
      StreetName: addressComp?.streetName || '',
      UnitNumber: addressComp?.unitNumber || '',
      propertyCity: addressComp?.city || '',
      propertyState: addressComp?.state || '',
      propertyZip: addressComp?.zip || '',
      Latitude: this.coordinateCtrl.value?.lat?.toString() ?? '',
      Longitude: this.coordinateCtrl.value?.lng?.toString() ?? '',
    });

    this._cdr.detectChanges();
  }

  checkDuplicateComp() {
    const loaderId = this.progressLoaderService.show('Checking for duplicates');
    this.comparableService
      .validateComparableExists({
        StreetAddress: this.customCompSearchForm.get('addressInfo.propertyStreetAddress')?.value
          ? this.customCompSearchForm.get('addressInfo.propertyStreetAddress')?.value
          : this.orderService.getUnparsedAddress(this.customCompSearchForm.get('addressInfo')?.value ?? null),
        UnitNumber: this.customCompSearchForm.get('addressInfo.UnitNumber')?.value ?? '',
        City: this.customCompSearchForm.get('addressInfo.propertyCity')?.value ?? '',
        StateOrProvince: this.customCompSearchForm.get('addressInfo.propertyState')?.value ?? '',
        PostalCode: this.customCompSearchForm.get('addressInfo.propertyZip')?.value ?? '',
      })
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        this.progressLoaderService.hide(loaderId);
        this.validationResult = res ?? null;
        if (this.validationResult?.isDuplicate == false) {
          this.addCustomComp();
        }
        this._cdr.detectChanges();
      });
  }

  onResetForm() {
    this.customCompSearchForm.reset();
    this.coordinateCtrl.reset();
    // Clear validation errors by marking the form as pristine and untouched
    this.customCompSearchForm.markAsPristine();
    this.customCompSearchForm.markAsUntouched();
    this.coordinateComponent?.syncValidationState();
    this.autocompleteComponent?.resetAutocomplete();
    this.validationResult = null;
    this._cdr.detectChanges();
  }

  processComparable() {
    // coordinateCtrl lives outside customCompSearchForm, so its validity has to be checked separately
    if (this.customCompSearchForm.invalid || this.coordinateCtrl.invalid) {
      // Trigger validation errors by marking all controls as touched and dirty
      this._markFormAsTouchedAndDirty(this.customCompSearchForm);
      this.coordinateCtrl.markAsTouched();
      this.coordinateComponent?.syncValidationState();
      this.snackBarService.open('warning', 'Please fill mandatory fields');
    } else {
      if (this.isSubject) {
        this.selectingSubject();
      } else if (this.comparableId) {
        this.confirmProperty();
      } else {
        if (this.isSpecificComparable) {
          this.addComp();
        } else {
          this.checkDuplicateComp();
        }
      }
    }
  }

  addComp() {
    const loaderId = this.progressLoaderService.show('Processing Comparable');

    const reqBody: AddComparableParams = {
      id: this.orderState.orderDetail.id,
      scenarioId: this.scenarioState.activeScenario!.id,
      ...(this.orderState.specificComparable?.ListingId
        ? { listingId: this.orderState.specificComparable.ListingId }
        : {}),
      ...(this.orderState.specificComparable?.PMXPropertyId
        ? { pmxPropertyId: this.orderState.specificComparable.PMXPropertyId }
        : {}),
      userRecord: this._getUpdatedFormValue(this.modifiedValues),
      valuationType: this.orderState.activeValuationType!,
    };

    this.addComparableService
      .addingComparableFromAddComp(reqBody)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        this.progressLoaderService.hide(loaderId);
        this._cdr.detectChanges();
        if (res) this.close.emit();
      });
  }

  addCustomComp() {
    const loaderId = this.progressLoaderService.show('Processing Comparable');
    const formValue = this.customCompSearchForm.getRawValue();
    const reqBody: AddNewCustomComparableParams = {
      id: this.orderState.orderDetail.id,
      scenarioId: this.scenarioState.activeScenario!.id,
      valuationType: this.orderState.activeValuationType!,
      userRecord: this._getUpdatedFormValue({ ...(formValue.addressInfo ?? {}), ...(formValue.compAttributes ?? {}) }),
    };

    reqBody.userRecord.propertyStreetAddress = reqBody.userRecord.propertyStreetAddress
      ? reqBody.userRecord.propertyStreetAddress
      : this.orderService.getUnparsedAddress(reqBody.userRecord);

    this.addComparableService
      .addingCustomComparableFromAddComp(reqBody)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        this.progressLoaderService.hide(loaderId);
        this._cdr.detectChanges();
        if (res) this.close.emit();
      });
  }

  selectingSubject() {
    const formValue = this.customCompSearchForm.getRawValue();
    const subjectDetails: AddNewCustomSubjectParams = {
      userRecord: this._getUpdatedFormValue({ ...(formValue.addressInfo ?? {}), ...(formValue.compAttributes ?? {}) }),
    };
    this.addSubjectManually.emit(subjectDetails);
  }

  confirmProperty() {
    const loaderId = this.progressLoaderService.show('Processing Comparable');
    const formValue = this.customCompSearchForm.getRawValue();
    const reqBody: ConfirmPropertyParam = {
      comparableId: this.comparableId,
      ...(this.isSpecificComparable
        ? { pmxPropertyId: this.orderState.specificComparable?.PublicRecord?.PMXPropertyId }
        : {}),
      userRecord: this.isSpecificComparable
        ? this._getUpdatedFormValue(this.modifiedValues)
        : this._getUpdatedFormValue({ ...(formValue.addressInfo ?? {}), ...(formValue.compAttributes ?? {}) }),
      orderId: this.orderState.orderDetail.id,
      scenarioId: this.scenarioState.activeScenario!.id,
    };

    this.addComparableService
      .confirmProperty(reqBody, this.orderState.activeValuationType!)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        this.progressLoaderService.hide(loaderId);
        this._cdr.detectChanges();
        if (res) this.close.emit();
      });
  }

  selectingComparable() {
    const loaderId = this.progressLoaderService.show('Selecting Comparable');

    if (this.validationResult)
      this.addComparableService
        .selectingComparableFromAddComp(this.validationResult)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe((res) => {
          this.progressLoaderService.hide(loaderId);
          this._cdr.detectChanges();
          if (res) this.close.emit();
        });
  }

  removingComparable() {
    const loaderId = this.progressLoaderService.show('Removing Comparable');

    if (this.validationResult)
      this.addComparableService
        .removeComparable(this.validationResult)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe((res) => {
          this.progressLoaderService.hide(loaderId);
          this._cdr.detectChanges();
          if (res) this.close.emit();
        });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  private _flattenFormControls(formGroup: FormGroup, initialValues: any, modifiedValues: { [key: string]: any }) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        // If it's a nested FormGroup, recursively flatten its controls
        this._flattenFormControls(control, initialValues[key], modifiedValues);
      } else if (control && control.value !== initialValues[key]) {
        // If it's a form control and the value has changed, add it to modifiedValues
        modifiedValues[key] = control.value;
      }
    });
  }

  private _getUpdatedFormValue(formValue: Partial<Record<PropertyAttribute, any>>) {
    if (Array.isArray(formValue?.derivedView)) formValue.derivedView = formValue?.derivedView?.join();
    return formValue;
  }

  /**
   * Recursively marks all controls in a form group as touched and dirty.
   * This triggers validation error display on all invalid fields.
   */
  private _markFormAsTouchedAndDirty(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this._markFormAsTouchedAndDirty(control);
      } else if (control) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }
}
